import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import axios from './axios'

interface useAuthProps {
  middleware?: 'guest' | 'auth'
  // redirectIfAuthenticated?: string
}

// eslint-disable-next-line no-unused-vars
type ErrorMessageHandler = (status?: number | null) => void
// eslint-disable-next-line no-unused-vars
type ErrorsHandler = (errors: string[]) => void
// type StatusHandler = (status: number | null) => void

interface LoginProps {
  setErrorMessage: ErrorMessageHandler
  setErrors: ErrorsHandler
  // setStatus: StatusHandler
}

interface ForgotPasswordProps {
  // email: string
  setErrorMessage: ErrorMessageHandler
  setErrors: ErrorsHandler
  // setStatus: StatusHandler
}

interface RegisterProps {
  setErrors: ErrorsHandler
}

export const useAuth = ({
  middleware,
  // redirectIfAuthenticated,
}: useAuthProps = {}) => {
  const [ready, setReady] = useState(false)
  const [isAuth, setIsAuth] = useState(false)
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const redirectTo = searchParams?.get('redirectTo')
    if (redirectTo && redirectTo !== '/') {
      window.sessionStorage.setItem('redirectTo', redirectTo)
      navigate(location.pathname, { replace: true })
    }
  }, [location.pathname, navigate, searchParams])

  const redirectIfAuthenticated =
    window.sessionStorage.getItem('redirectTo') || '/'

  const redirectTo = useMemo(() => {
    const redirectTo = [location.pathname, location.search, location.hash].join(
      ''
    )
    if (redirectTo && redirectTo !== '/') return redirectTo
  }, [location])

  const loginURL = useMemo(() => {
    return [
      '/login',
      redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : '',
    ]
      .filter(Boolean)
      .join('')
  }, [redirectTo])

  const {
    data: user,
    error,
    mutate,
  } = useSWR('/api/user', async () => {
    return axios
      .get('/api/user')
      .then((res) => res.data)
      .catch((error) => {
        if (
          error.response.status === 401 &&
          !/^\/(login|forgot-password|reset-password)/.test(location.pathname)
        ) {
          setIsAuth(false)
          navigate(loginURL, { replace: true, state: { from: location } })
        }
        if (error.response.status !== 409) {
          throw error
        }
        // router.push('/verify-email')
      })
  })

  // const isAuth = useMemo(() => !!user, [user])

  const csrf = () => axios.get('/sanctum/csrf-cookie')

  const register = useCallback(
    async ({ setErrors, ...props }: RegisterProps) => {
      await csrf()
      setErrors([])
      axios
        .post('/register', props)
        .then(() => mutate())
        .catch((error) => {
          if (error.response.status !== 422) throw error
          setErrors(error.response.data.errors)
        })
    },
    [mutate]
  )

  const login = useCallback(
    async ({ setErrorMessage, setErrors, ...props }: LoginProps) => {
      await csrf()
      setErrorMessage?.()
      setErrors?.([])
      axios
        .post('/login', props)
        .then(() => mutate())
        .catch((error) => {
          if (error.response.status !== 422) throw error
          setErrorMessage?.(error.response.data.message ?? t('auth.error'))
          setErrors?.(error.response.data.errors)
        })
    },
    [mutate, t]
  )

  const forgotPassword = useCallback(
    async ({ setErrorMessage, setErrors, ...props }: ForgotPasswordProps) => {
      await csrf()
      setErrorMessage?.()
      setErrors?.([])
      try {
        const response = await axios.post('/forgot-password', props)
        return response.data
      } catch (err: any) {
        if (err.response.status !== 422) throw err
        setErrorMessage?.(err.response.data.message ?? t('auth.error'))
        setErrors?.(err.response.data.errors)
      }
    },
    [t]
  )

  const resetPassword = useCallback(
    async ({ setErrorMessage, setErrors, ...props }: ForgotPasswordProps) => {
      await csrf()
      setErrorMessage?.()
      setErrors?.([])
      try {
        const result = await axios.post('/reset-password', props)
        mutate()
        return result
      } catch (err: any) {
        if (err.response.status !== 422) throw err
        setErrorMessage?.(err.response.data.message ?? t('auth.error'))
        setErrors?.(err.response.data.errors)
      }
    },
    [mutate, t]
  )

  const logout = useCallback(async () => {
    if (!error) {
      await axios.post('/logout').then(() => mutate())
    }
    window.location.pathname = '/login'
  }, [error, mutate])

  const render = useCallback(
    (forAuth: any, forNotAuth: any) => {
      if (ready) {
        return isAuth ? forAuth : forNotAuth
      }
      return null
    },
    [isAuth, ready]
  )

  useEffect(() => {
    const isAuth = !!user && !error
    setIsAuth(!!user && !error)
    setReady(!!user || !!error)

    if (middleware === 'guest' && redirectIfAuthenticated && isAuth) {
      navigate(redirectIfAuthenticated, { replace: true })
      window.sessionStorage.removeItem('redirectTo')
    }
    if (
      window.location.pathname === '/verify-email' &&
      user?.email_verified_at
    ) {
      // router.push(redirectIfAuthenticated)
    }
    if (middleware === 'auth' && error) logout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, error])

  return useMemo(
    () => ({
      ready,
      isAuth,
      user,
      register,
      login,
      loginURL,
      forgotPassword,
      resetPassword,
      logout,
      render,
    }),
    [
      forgotPassword,
      isAuth,
      login,
      loginURL,
      logout,
      ready,
      register,
      render,
      resetPassword,
      user,
    ]
  )
}
